<template>
  <div
    v-if="
      report &&
      report.customStatistics.viewpointsItems &&
      report.customStatistics.viewpointsItems.length === 0
    "
  >
    <p>
      Once you invite colleagues to complete Viewpoints feedback their perspectives will be shown
      here
    </p>
  </div>
  <div v-else-if="report && !report.customStatistics.viewpointsItems">
    <p>You and your colleagues need more Viewpoints responses to see your Viewpoints section</p>
  </div>
  <div v-else-if="report && report.customStatistics.viewpointsItems[0].count < 2">
    <p>You need at least two Viewpoints responses to see your Viewpoints section</p>
  </div>
  <div
    class="custom-viewpoints-bar-graph"
    v-else-if="report && report.customStatistics.viewpointsItems[0].count >= 2"
  >
    <div>
      <div style="height: 50px"></div>

      <div :class="row.id" :key="row.id" v-for="row in rows">
        <a
          href="javascript:void(0)"
          @click="click(row)"
          class="a-block customValuesGraphRowAnchor cs-accordion-anchor"
        >
          <div class="customValuesGraph row cs-accordion-anchor-child">
            <div
              class="customValuesGraphLabel"
              style="line-height: 1; display: flex; align-items: center; color: #404040"
            >
              <div>
                {{ row.shortTitle }}
              </div>
            </div>
            <div class="customValuesGraphArea">
              <div class="customValuesCutoff" style="right: 0%"></div>
              <div class="customValuesCutoff" style="right: 25%"></div>
              <div class="customValuesCutoff" style="left: 50%"></div>
              <div class="customValuesCutoff" style="left: 25%"></div>
              <div class="customValuesCutoff" style="left: 0%"></div>

              <Tooltip :text="tooltip(row)">
                <span class="valueGraphBar" :style="bar(row.min, row.max, 5)"></span>
              </Tooltip>

              <Tooltip :text="tooltip(row)">
                <span class="valueGraphBar" :style="diamond(row.mean)"></span>
              </Tooltip>
              <Tooltip :text="tooltip(row, true)">
                <span class="valueGraphBar marker" :style="marker(row.self)"></span>
              </Tooltip>
            </div>
          </div>
        </a>

        <div
          :class="{ hidden: row.id !== activeRowId }"
          class="customValuesGraphInfo cs-accordion-panel"
        >
          <hr />
          <div class="row">
            <p class="col-sm-7 text-xs-center text-sm-left">
              {{ row.content }}
            </p>
            <div class="col-sm-5 text-center">
              <p style="font-size: 20px">Mean Rating:</p>
              <p style="font-size: 50px">{{ row.mean }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="customValuesGraphXAxis">
        <div class="customValuesGraphXAxisLabels">
          <div class="customValuesGraphXAxisLabel" style="left: 75%; width: 25%">
            Highly characteristic
          </div>
          <div class="customValuesGraphXAxisLabel" style="left: 50%; width: 25%">
            More characteristic
          </div>
          <div class="customValuesGraphXAxisLabel" style="left: 25%; width: 25%">
            Less characteristic
          </div>
          <div class="customValuesGraphXAxisLabel" style="left: 0%; width: 25%">
            Not characteristic
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, Ref, ref, inject } from "vue";
import { Tooltip } from "uiv";
import CustomRow from "./custom-row.interface";
import { CustomSoloReportApiModel } from "../../api-models";

const report = inject("report") as Ref<CustomSoloReportApiModel>;

const values = inject("values") as Ref<string[] | undefined>;

const activeRowId = ref("");

const click = (row: CustomRow | null): void => {
  activeRowId.value = row?.id === activeRowId.value ? "" : row?.id ?? "";
};

function startingActiveRow(rows: CustomRow[]): void {
  activeRowId.value =
    rows.find((row) => row.mean === Math.max(...rows.map((r) => r.mean)))?.id ?? "";
}

const bar = (start: number, end: number, height: number): string => {
  return `position: absolute;
    left: ${start}%;
    right: ${end}%;
    width: ${end - start}%;
    border-top-width: ${height}px;
    height: ${height}px;
    top: ${(50 - height) / 2}px;
    `;
};

const diamond = (x: number): string => {
  const size = 30;

  return `position: absolute;
    left: calc(${x}% - ${size / 2}px);
    width: ${size}px;
    background: white;
    border-width: 5px;
    height: ${size}px;
    transform:rotate(45deg);
    top: 10px;
    `;
};

const marker = (x: number): string => {
  const size = 30;

  return `position: absolute;
    left: ${x}%;
    width: 10px;
    background: white;
    border-width: 5px;
    height: ${size}px;
    top: 10px;
    `;
};

const tooltip = (row: CustomRow, self?: boolean): string => {
  return self
    ? "Your rating"
    : `Your respondents rated ‘${row.shortTitle}’ between ${row.min} and ${row.max} with an average of ${row.mean}`;
};

const score = (id: string, name: string): CustomRow => {
  const statsForItem = report.value?.customStatistics.viewpointsItems.find((i) =>
    i.itemId.includes(id),
  );
  const q1 = Math.round(statsForItem?.lowerQuartile ?? 0);
  const min = statsForItem?.min ?? 0;
  const median = statsForItem?.median ?? 0;
  const q3 = Math.round(statsForItem?.upperQuartile ?? 0);
  const max = statsForItem?.max ?? 0;
  const mean = Math.round(statsForItem?.mean ?? 0);

  const self =
    report.value?.userValues.ratings.find((i) => i.metadata.title === name)?.rawRating ?? 0;

  return {
    id,
    shortTitle: name,
    content: `${values?.value?.find((v) => v.includes(name)) ?? name}`,
    min,
    mean,
    q1,
    median,
    q3,
    max,
    self,
  };
};

const rows = computed(() => {
  if (!report.value) {
    return [];
  }

  const reportRows: CustomRow[] = [];
  report.value.userValues.ratings.forEach((rating) => {
    reportRows.push(score(rating.metadata.id, rating.metadata.title));
  });
  startingActiveRow(reportRows);
  return reportRows;
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.tooltip {
  z-index: 3 !important;
}

.custom-viewpoints-bar-graph {
  position: relative;
  padding-bottom: 20px;

  .scale {
    margin: 0 auto;
    margin-top: 5px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding-left: 30px;
    padding-right: 30px;

    & > span {
      display: block;
      width: 8em;
      text-align: center;

      &:first-child {
        text-align: left;
      }

      &:last-child {
        text-align: right;
      }
    }
  }
}
</style>
